import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  ListGroup,
  Badge
} from 'react-bootstrap';
import { CheckCircleFill, ShieldLock } from 'react-bootstrap-icons';
import TesterBudNavbar from '../../subComponent/TesterBudNavBar'; // Removed, assuming it is not available
import HeroImage from '../icons/form-icon.png';       // Removed, using placeholder

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [secretCode, setSecretCode] = useState('');
  const [currentPassword, setCurrentPassword, ] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [step, setStep] = useState(1);
  const correctEmail = 'user@premiumbank.com';
  const correctSecretCode = 'BANK1234';
  const correctPassword = 'Bank@123';
  const passwordRules = {
    minLength: 8,
    hasUpperCase: /[A-Z]/.test(newPassword),
    hasLowerCase: /[a-z]/.test(newPassword),
    hasNumber: /[0-9]/.test(newPassword),
    hasSpecialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(newPassword),
  };
  const isPasswordValid = () => {
    return (
      newPassword.length >= passwordRules.minLength &&
      passwordRules.hasUpperCase &&
      passwordRules.hasLowerCase &&
      passwordRules.hasNumber &&
      passwordRules.hasSpecialChar
    );
  };
  const handleSubmitEmail = (e) => {
    e.preventDefault();
    setError('');
    if (!email) return setError('Email is required');
    if (email !== correctEmail) return setError('Email not found in our records');
    setStep(2);
  };
  const handleSubmitCode = (e) => {
    e.preventDefault();
    setError('');
    if (!secretCode) return setError('Secret code is required');
    if (secretCode !== correctSecretCode) return setError('Invalid secret code');
    setStep(3);
  };
  const handleSubmitPassword = (e) => {
    e.preventDefault();
    setError('');
    if (!currentPassword || !newPassword || !confirmPassword) return setError('All fields are required');
    if (newPassword !== confirmPassword) return setError('Passwords do not match');
    if (!isPasswordValid()) return setError('Password does not meet requirements');
    setSuccess(true);
  };


  return (
    <HelmetProvider>
      <Helmet>
        <title>Forgot Password Practice | Reset Password Automation</title>
        <meta http-equiv="Content-type" content="text/html;charset=UTF-8" />
        <meta name="description" content="Practice automating the forgot password flow.  Learn to automate password resets with Selenium, Cypress, and Playwright.  Ideal for QA automation practice." />
        <meta name="keywords" content="forgot password automation, reset password automation, password reset practice, selenium forgot password, cypress reset password, playwright forgot password, forgot password testing, reset password testing, password recovery automation, forgot password practice, automate password reset" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="TesterBud Team" />
        <meta property="og:title" content="Forgot Password Practice | Reset Password Automation" />
        <meta property="og:description" content="Practice automating the forgot password flow.  Learn to automate password resets with Selenium, Cypress, and Playwright.  Ideal for QA automation practice." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://testerbud.com/practice-forgot-password" />
        <meta name="geo.region" content="GLOBAL" />
        <meta name="geo.placename" content="Global" />
      </Helmet>
      <Container fluid className="hero-section bg-primary text-white py-5" style={{ position: 'relative', overflow: 'hidden' }}>
        <Row className="align-items-center justify-content-center">
          <Col md={6} className="text-center text-md-start">
            <h1 className="display-4 fw-bold mb-3">Master Password Recovery Automation</h1>
            <p className="lead mb-4">
              Practice automating the forgot password flow and enhance your software testing skills.  Perfect for Selenium, Cypress, and Playwright learners.
            </p>
            <p className="lead mb-4">
              Develop hands-on experience with password reset processes and improve your QA automation expertise. 🚀
            </p>
          </Col>
          <Col md={5} className="d-none d-md-block text-center">
            <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}>
              <div
                style={{
                  width: '50%',
                  height: '50%',
                  borderRadius: '0%',
                  overflow: 'auto',
                  marginTop: '-4%',
                  marginRight: '10%',
                  marginBottom: '-4%',
                }}
              >
                <img src={HeroImage} alt="Password Reset Illustration" className="img-fluid" style={{  // Using placeholder
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }} />
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    <TesterBudNavbar />
      <Container fluid className="min-vh-100 d-flex flex-column justify-content-center">
        <Row className="justify-content-center">
          <Col md={8} lg={6} xl={4}>
            {/* Header */}
            <div className="text-center mb-5">
              <h1 className="text-white display-5 fw-bold mb-3">
                <ShieldLock className="me-2" />
                Premium Bank
              </h1>
              <p className="text-white-50">Secure Banking Solutions</p>
            </div>

            {/* Step Indicator */}
            <div className="d-flex justify-content-center mb-4">
              {[1, 2, 3].map((num) => (
                <Badge
                  key={num}
                  bg={step >= num ? 'primary' : 'secondary'}
                  className="rounded-circle mx-2"
                  style={{ width: '1.5rem', height: '1.5rem' }}
                >
                  {num}
                </Badge>
              ))}
            </div>
            <Card className="shadow-lg mb-4">
              <Card.Body className="p-4">

                <p className="text-muted mb-4">
                  This page provides a practice environment for automating the password recovery process. You can use this to hone your skills in automating workflows involving email verification, security code entry, and password resets.
                </p>
              </Card.Body>
            </Card>

            {success ? (
              /* Success Card */
              <Card className="shadow-lg">
                <Card.Body className="text-center p-4">
                  <CheckCircleFill className="text-success mb-3" size={64} />
                  <h2 className="mb-3">Password Changed Successfully!</h2>
                  <p className="text-muted mb-4">
                    Your password has been updated.  You can now log in with your new password.
                  </p>
                  <Link
                    to="/practice-login-form"
                    variant="primary"
                    className="w-100 btn btn-primary"
                  >
                    Return to Login
                  </Link>
                </Card.Body>
              </Card>
            ) : (
              <Card className="shadow-lg">
                <Card.Body className="p-4">
                  <h3 className="text-center mb-4">
                    {step === 1 && 'Forgot Password'}
                    {step === 2 && 'Verify Security Code'}
                    {step === 3 && 'Reset Password'}
                  </h3>


                  {step === 1 && (
                    <>
                      <p className="mb-4">
                        Enter your registered email address to receive a security code.  This practice page helps you automate this process.
                      </p>
                    </>
                  )}
                  {step === 2 && (
                    <>
                      <p className="mb-4">
                        Enter the security code sent to your email.  Practice automating code verification.
                      </p>
                    </>
                  )}
                  {step === 3 && (
                    <>
                      <p className="mb-4">
                        Enter your current password and set a new password.  Use this to practice automating password resets.
                      </p>
                    </>
                  )}

                  <Alert variant="info" className="mb-4">
                    <Alert.Heading>Demo Credentials for Automation Practice</Alert.Heading>
                    <p className="mb-1">
                      <strong>Email:</strong> {correctEmail}
                    </p>
                    <p className="mb-1">
                      <strong>Secret Code:</strong> {correctSecretCode}
                    </p>
                    <p className="mb-0">
                      <strong>Old Password:</strong> {correctPassword}
                    </p>
                  </Alert>

                  {error && <Alert variant="danger">{error}</Alert>}

                  {/* Step 1: Email Form */}
                  {step === 1 && (
                    <Form onSubmit={handleSubmitEmail}>
                      <Form.Group className="mb-3">
                        <Form.Label>Email Address</Form.Label>
                        <Form.Control
                          type="email"
                          placeholder="Enter your registered email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                      </Form.Group>
                      <div className="d-grid gap-2">
                        <Button variant="primary" type="submit">
                          Continue
                        </Button>
                        <Link
                          to="/practice-login-form"
                          variant="outline-primary"
                          className="btn btn-outline-primary"
                        >
                          Back to Login
                        </Link>
                      </div>
                    </Form>
                  )}

                  {/* Step 2: Secret Code Form */}
                  {step === 2 && (
                    <Form onSubmit={handleSubmitCode}>
                      <Form.Group className="mb-3">
                        <Form.Label>Security Code</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter security code"
                          value={secretCode}
                          onChange={(e) => setSecretCode(e.target.value)}
                        />
                      </Form.Group>
                      <div className="d-grid">
                        <Button variant="primary" type="submit" className="btn btn-primary">
                          Verify Code
                        </Button>
                      </div>
                    </Form>
                  )}

                  {/* Step 3: Password Reset Form */}
                  {step === 3 && (
                    <Form onSubmit={handleSubmitPassword}>
                      <Form.Group className="mb-3">
                        <Form.Label>Current Password</Form.Label>
                        <Form.Control
                          type="password"
                          placeholder="Current password"
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                        />
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>New Password</Form.Label>
                        <Form.Control
                          type="password"
                          placeholder="New password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                        />
                      </Form.Group>

                      <Form.Group className="mb-4">
                        <Form.Label>Confirm New Password</Form.Label>
                        <Form.Control
                          type="password"
                          placeholder="Confirm new password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                        />
                      </Form.Group>

                      <div className="mb-4">
                        <h6 className="mb-2">Password Requirements for Automation</h6>
                        <ListGroup>
                          <ListGroup.Item className={newPassword.length >= 8 ? 'text-success' : 'text-danger'}>
                            Minimum 8 characters
                          </ListGroup.Item>
                          <ListGroup.Item className={passwordRules.hasUpperCase ? 'text-success' : 'text-danger'}>
                            At least one uppercase letter
                          </ListGroup.Item>
                          <ListGroup.Item className={passwordRules.hasLowerCase ? 'text-success' : 'text-danger'}>
                            At least one lowercase letter
                          </ListGroup.Item>
                          <ListGroup.Item className={passwordRules.hasNumber ? 'text-success' : 'text-danger'}>
                            At least one number
                          </ListGroup.Item>
                          <ListGroup.Item className={passwordRules.hasSpecialChar ? 'text-success' : 'text-danger'}>
                            At least one special character
                          </ListGroup.Item>
                        </ListGroup>
                      </div>

                      <div className="d-grid">
                        <Button variant="primary" type="submit" className="btn btn-primary">
                          Reset Password
                        </Button>
                      </div>
                    </Form>
                  )}
                </Card.Body>
              </Card>
            )}

            {/* Footer */}
            <div className="text-center mt-4">
              <p className="text-white-50 small mb-1">
                © 2025 Premium Bank. All rights reserved.
              </p>
              <p className="text-white-50 small">
                Secure 256-bit encrypted connection
              </p>
              <Link
                to="/"
                variant="light"
                size="sm"
                className="mt-2 btn btn-light btn-sm"
              >
                ← Back to Practice UI Automation Page
              </Link>
            </div>
          </Col>
        </Row>
      </Container>
      <footer className="bg-dark text-light text-center py-4 mt-auto">
        <Container>
          <p className="mb-1">Ready to start automating? Explore, practice, and master!</p>
          <small>© 2025 TesterBud | Built for learners by learners 🚀</small>
        </Container>
      </footer>
    </HelmetProvider>
  );
};



export default ForgotPasswordPage;
